/**
 * Lightweight WebAudio-based ambient mixer.
 *
 * Plays curated Sound Library assets and schedules engine-owned environmental
 * events using the Web Audio API.
 * Designed to play UNDER the ElevenLabs dialogue without competing with it.
 *
 * Missing assets are intentionally silent; procedural ambience is no longer the
 * default replacement for the curated library.
 */
// BREATHING presence calibration (stabilization pass, measured in-browser):
// the ambience beds land at ≈ -46 dBFS (wind) to -59 dBFS (fire) effective
// RMS, while the breathing recordings are extremely quiet at source
// (-51..-57 dBFS RMS) — a fixed low gain made them inaudible. The level is
// therefore NORMALIZED at runtime from the decoded PCM to a perceptual
// target just under the wind bed: noticeable in meaningful silence, never a
// separate "track". While dialogue plays, breathing is ducked well below it.
const BREATHING_TARGET_RMS = 0.0024; // ≈ -52 dBFS into the master bus (device-tuned: audible in silence, below the beds)
const BREATHING_DUCK_MULT = 0.22;   // ≈ -13 dB while the companion speaks
const BREATHING_MAX_GAIN = 8;       // safety cap for degenerate files

class AmbientMixer {
  constructor() {
    this.ctx = null;
    this.masterGain = null;
    this.bedNodes = [];
    this.presenceTimer = null;
    this.soundPlan = null;
    this.environmentState = null;
    this.running = false;
    // Iter 90 — default atmosphere level is 50% of full scale; duck/unduck
    // multipliers are applied relative to this master level.
    this.masterLevel = 0.5;
    this.duckTarget = this.masterLevel;
    this.onEvent = null; // callback(category) fired when a world event actually plays
    this._paused = false;
    this._lastAmbientEvents = null;
  }

  ensureContext() {
    if (this.ctx) return;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    this.ctx = new AudioCtx();
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = 0;
    const hidden = typeof document !== "undefined" && document.hidden;
    // TWO output taps, crossfaded by page visibility so exactly ONE is audible
    // at any moment (no doubling, no whine) AND audio survives a locked screen:
    //   • speakerGain → ctx.destination — clean PCM, audible while VISIBLE.
    //   • streamGain  → MediaStreamDestination → a hidden <audio srcObject>
    //     that is ALWAYS playing (keep-alive) but carries SILENCE while visible;
    //     it becomes audible only when the page is HIDDEN (screen off / lock),
    //     where the OS would otherwise suspend ctx.destination.
    // The <audio> element playing continuously is what keeps the AudioContext
    // alive under the lock screen — a muted element does NOT, which is why
    // audio previously died when the screen turned off.
    this.speakerGain = this.ctx.createGain();
    this.speakerGain.gain.value = hidden ? 0 : 1;
    this.masterGain.connect(this.speakerGain);
    this.speakerGain.connect(this.ctx.destination);
    try {
      this.streamDest = this.ctx.createMediaStreamDestination();
      this.streamGain = this.ctx.createGain();
      this.streamGain.gain.value = hidden ? 1 : 0;
      this.masterGain.connect(this.streamGain);
      this.streamGain.connect(this.streamDest);
    } catch (e) {
      this.streamDest = null;
      this.streamGain = null;
      // No stream mirror available — speaker must stay audible regardless.
      this.speakerGain.gain.value = 1;
    }
    // Iter 86 — LOCK-SCREEN RESILIENCE. Dialogue survives the lock screen
    // because it is a plain HTMLAudioElement; the ambient bed lives on this
    // AudioContext, which the OS may suspend/"interrupt" when the screen
    // locks even though the hidden keep-alive element keeps playing (it then
    // carries silence). Watch the context state and aggressively resume
    // whenever the mix is supposed to be audible. The OS can also pause the
    // keep-alive element itself on lock — restart it in the same loop.
    this._userPaused = false;
    this._resumeRetryTimer = null;
    this._onCtxStateChange = () => {
      const tryResume = () => {
        if (!this.ctx || this._userPaused || this.ctx.state === "running" || this.ctx.state === "closed") {
          if (this._resumeRetryTimer) { clearInterval(this._resumeRetryTimer); this._resumeRetryTimer = null; }
          return;
        }
        this.ctx.resume().catch(() => { /* retried by interval */ });
        if (this._bgAudioEl && this._bgAudioEl.paused) {
          const p = this._bgAudioEl.play();
          if (p && p.catch) p.catch(() => { /* ignore */ });
        }
      };
      tryResume();
      if (this.ctx && this.ctx.state !== "running" && this.ctx.state !== "closed" && !this._userPaused && !this._resumeRetryTimer) {
        this._resumeRetryTimer = setInterval(tryResume, 1000);
      }
    };
    try { this.ctx.onstatechange = this._onCtxStateChange; } catch (e) { /* ignore */ }
    // Iter 87 — persistent lock-screen watchdog. `onstatechange` does not
    // fire reliably on iOS "interrupted" transitions, so a low-cost interval
    // continuously heals the context + keep-alive element while the mix is
    // supposed to be audible. Cleared on stop().
    if (!this._lockWatchdogTimer) {
      this._lockWatchdogTimer = setInterval(() => {
        if (!this.ctx || this._userPaused || this.ctx.state === "closed") return;
        if (this.ctx.state !== "running") this.ctx.resume().catch(() => { /* keep retrying */ });
        if (this._bgAudioEl && this._bgAudioEl.paused) {
          const p = this._bgAudioEl.play();
          if (p && p.catch) p.catch(() => { /* retried next tick */ });
        }
      }, 2000);
    }
  }

  /** Returns the MediaStream that mirrors the ambient mix. Attach this to a
   *  hidden <audio> element's srcObject for background-resilient playback. */
  getMediaStream() {
    this.ensureContext();
    return this.streamDest ? this.streamDest.stream : null;
  }

  // Crossfade the audible path between the speaker (visible) and the always-on
  // stream mirror (hidden). Short ramps avoid clicks; overlap is imperceptible.
  _applyVisibilityRouting(hidden) {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    const RAMP = 0.25;
    if (this.speakerGain) {
      this.speakerGain.gain.cancelScheduledValues(now);
      this.speakerGain.gain.setValueAtTime(this.speakerGain.gain.value, now);
      this.speakerGain.gain.linearRampToValueAtTime(hidden ? 0 : 1, now + RAMP);
    }
    if (this.streamGain) {
      this.streamGain.gain.cancelScheduledValues(now);
      this.streamGain.gain.setValueAtTime(this.streamGain.gain.value, now);
      this.streamGain.gain.linearRampToValueAtTime(hidden ? 1 : 0, now + RAMP);
    }
    if (this.ctx.state === "suspended") this.ctx.resume().catch(() => { /* ignore */ });
  }

  /**
   * Wire a hidden <audio> element to the mix for lock-screen resilience.
   *
   * The element is srcObject=stream, UNMUTED, and kept PLAYING at all times so
   * the browser keeps the audio session (and the AudioContext) alive when the
   * screen turns off. It carries SILENCE while the page is visible (streamGain
   * is 0 then) so there is no doubling and no whine; it becomes the sole audible
   * path only when the page is hidden. Speaker vs stream are crossfaded on
   * visibilitychange so exactly one is ever audible.
   */
  attachBackgroundAudio(audioEl) {
    if (!audioEl) return false;
    const stream = this.getMediaStream();
    if (!stream) return false;
    this._bgAudioEl = audioEl;
    audioEl.srcObject = stream;
    audioEl.muted = false;
    audioEl.volume = 1;
    audioEl.setAttribute("playsinline", "");
    const p = audioEl.play();
    if (p && p.catch) p.catch(() => { /* retried on visibilitychange */ });
    // Iter 86 — the OS may pause the keep-alive element on lock (media-session
    // arbitration with the dialogue element). Restart it unless the USER paused.
    audioEl.onpause = () => {
      if (!this._userPaused && this._bgAudioEl === audioEl) {
        const rp = audioEl.play();
        if (rp && rp.catch) rp.catch(() => { /* retried by state-change loop */ });
      }
    };
    this._applyVisibilityRouting(!!document.hidden);
    this._visHandler = () => {
      // Ensure the keep-alive element is (still) playing on every transition.
      if (this._bgAudioEl) {
        const pp = this._bgAudioEl.play();
        if (pp && pp.catch) pp.catch(() => { /* ignore */ });
      }
      this._applyVisibilityRouting(!!document.hidden);
    };
    document.addEventListener("visibilitychange", this._visHandler);
    return true;
  }
  _makeNoiseBuffer(type = "brown", lengthSec = 4) {
    const sampleRate = this.ctx.sampleRate;
    const buffer = this.ctx.createBuffer(1, sampleRate * lengthSec, sampleRate);
    const data = buffer.getChannelData(0);
    if (type === "pink") {
      let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
      for (let i = 0; i < data.length; i++) {
        const w = Math.random() * 2 - 1;
        b0 = 0.99886 * b0 + w * 0.0555179;
        b1 = 0.99332 * b1 + w * 0.0750759;
        b2 = 0.96900 * b2 + w * 0.1538520;
        b3 = 0.86650 * b3 + w * 0.3104856;
        b4 = 0.55000 * b4 + w * 0.5329522;
        b5 = -0.7616 * b5 - w * 0.0168980;
        data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11;
        b6 = w * 0.115926;
      }
    } else { // brown
      let last = 0;
      for (let i = 0; i < data.length; i++) {
        const w = Math.random() * 2 - 1;
        last = (last + 0.02 * w) / 1.02;
        data[i] = last * 3.5;
      }
    }
    return buffer;
  }

  _startBed(envSynth) {
    const ctx = this.ctx;
    const noise = this._makeNoiseBuffer(envSynth.noise || "brown", 6);
    const src = ctx.createBufferSource();
    src.buffer = noise;
    src.loop = true;

    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = envSynth.lowpass_hz || 800;
    lp.Q.value = 0.4;

    const slowLFO = ctx.createOscillator();
    const slowGain = ctx.createGain();
    slowLFO.frequency.value = 0.07; // very slow breath of the world
    slowGain.gain.value = 0.10;

    const bedGain = ctx.createGain();
    bedGain.gain.value = 0.55;

    slowLFO.connect(slowGain);
    slowGain.connect(bedGain.gain);

    src.connect(lp);
    lp.connect(bedGain);
    bedGain.connect(this.masterGain);

    src.start();
    slowLFO.start();

    this.bedNodes.push(src, slowLFO, bedGain, lp, slowGain);
  }

  _fireClicks(env) {
    // Occasional ember pops if fire_clicks is true.
    if (!env.fire_clicks) return;
    const ctx = this.ctx;
    const tick = () => {
      if (!this.running) return;
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.value = 600 + Math.random() * 400;
      g.gain.value = 0;
      g.gain.setValueAtTime(0, ctx.currentTime);
      g.gain.linearRampToValueAtTime(0.06, ctx.currentTime + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.18);
      osc.connect(g);
      g.connect(this.masterGain);
      osc.start();
      osc.stop(ctx.currentTime + 0.2);
      const next = 2200 + Math.random() * 4800;
      setTimeout(tick, next);
    };
    setTimeout(tick, 1200);
  }

  _startPresenceBreath(periodSec = 14) {
    const ctx = this.ctx;
    const tick = () => {
      if (!this.running) return;
      // A very soft warm sine swell — feels like the companion's slow breath nearby
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = 130 + Math.random() * 30;
      const now = ctx.currentTime;
      g.gain.setValueAtTime(0, now);
      g.gain.linearRampToValueAtTime(0.025, now + 1.6);
      g.gain.linearRampToValueAtTime(0, now + 4.2);
      osc.connect(g);
      g.connect(this.masterGain);
      osc.start(now);
      osc.stop(now + 4.4);
      const jitter = periodSec * (0.85 + Math.random() * 0.3);
      this.presenceTimer = setTimeout(tick, jitter * 1000);
    };
    this.presenceTimer = setTimeout(tick, 2400);
  }

  async start({ environment, presence, soundPlan, environmentState, fadeInSec }) {
    this.ensureContext();
    if (this.ctx.state === "suspended") {
      try { await this.ctx.resume(); } catch (e) { console.warn("ambient: ctx.resume failed", e); }
    }
    if (this.running) return;
    this.running = true;
    this.soundPlan = soundPlan || environment?.sound_plan || null;
    this.environmentState = environmentState || environment?.environment_state || null;

    const env = environment?.synth || soundPlan?.synth || { noise: "brown", lowpass_hz: 700 };
    const loopAssets = soundPlan?.ambient_loops?.assets || environment?.assets || [];
    const playableLoops = this._playableAssets(loopAssets);
    if (playableLoops.length) {
      this._startAmbientLoops(playableLoops);
    } else if (soundPlan?.procedural_replacement_enabled) {
      this._startBed(env);
      this._fireClicks(env);
    }
    if (presence?.breath_period_sec) this._startPresenceBreath(presence.breath_period_sec);
    if (soundPlan?.ambient_events) this.scheduleAmbientEvents(soundPlan.ambient_events);

    // Gentle fade-in (morning sessions pass a slower 5s fade — the world returns softly)
    const now = this.ctx.currentTime;
    this.masterGain.gain.cancelScheduledValues(now);
    this.masterGain.gain.setValueAtTime(this.masterGain.gain.value || 0, now);
    this.masterGain.gain.linearRampToValueAtTime(this.duckTarget, now + (fadeInSec || 2.5));
  }

  async _startAmbientLoops(assets = []) {
    const playable = this._playableAssets(assets);
    if (!playable.length) return;
    playable.forEach((asset) => {
      this.playSampleLoop(asset.storage.url, asset.target_gain || 0.32).catch((e) => {
        console.warn("ambient loop asset failed", asset.category, e);
      });
    });
  }

  _playableAssets(assets = []) {
    return assets.filter((asset) => asset?.storage?.url);
  }

  async playSampleLoop(url, gain = 0.32) {
    if (!this.ctx || !this.masterGain || !url) return null;
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const arrayBuffer = await response.arrayBuffer();
      const buffer = await this.ctx.decodeAudioData(arrayBuffer);

      // A dedicated gain for this loop asset — holds the target loudness and
      // performs the gentle 4s fade-in. Stays connected for the whole session.
      const assetGain = this.ctx.createGain();
      const target = Math.max(0, Math.min(1, gain));
      const now = this.ctx.currentTime;
      assetGain.gain.setValueAtTime(0.0001, now);
      assetGain.gain.linearRampToValueAtTime(target, now + 4);
      assetGain.connect(this.masterGain);
      this.bedNodes.push(assetGain);

      // SEAMLESS CROSSFADE LOOP — instead of `src.loop = true` (which clicks at
      // the MP3 encoder-padding seam), we retrigger the SAME buffer slightly
      // before it ends and crossfade the two overlapping instances. The listener
      // hears one endless recording, never a restart.
      const dur = buffer.duration;
      const xfade = Math.min(2.0, Math.max(0.15, dur * 0.2));
      const period = Math.max(0.1, dur - xfade); // gap between successive starts
      this._loopTimers = this._loopTimers || [];
      const gen = this._loopGen || 0; // Iter 92 — crossfadeToLoops bumps the generation to retire old loops

      const scheduleFrom = (startTime) => {
        if (!this.running || gen !== (this._loopGen || 0)) return;
        const src = this.ctx.createBufferSource();
        src.buffer = buffer;
        const eg = this.ctx.createGain();
        src.connect(eg);
        eg.connect(assetGain);
        // Fade in over xfade, hold, fade out over xfade → overlaps the next instance.
        eg.gain.setValueAtTime(0.0001, startTime);
        eg.gain.linearRampToValueAtTime(1, startTime + xfade);
        eg.gain.setValueAtTime(1, Math.max(startTime + xfade, startTime + dur - xfade));
        eg.gain.linearRampToValueAtTime(0.0001, startTime + dur);
        src.start(startTime);
        src.stop(startTime + dur + 0.05);
        src.onended = () => {
          try { src.disconnect(); eg.disconnect(); } catch (e) { /* ignore */ }
        };
        // Schedule the next overlapping instance a touch before this one ends.
        const nextStart = startTime + period;
        const delayMs = (nextStart - xfade - this.ctx.currentTime) * 1000;
        const t = setTimeout(() => scheduleFrom(nextStart), Math.max(0, delayMs));
        this._loopTimers.push(t);
      };
      scheduleFrom(this.ctx.currentTime + 0.05);
      return assetGain;
    } catch (e) {
      console.warn("ambient loop fetch/decode failed", e);
      return null;
    }
  }

  /**
   * Iter 92 — Morning Continuum: fade every current ambient loop bed out and
   * bring a new loop set in (owls hand over to birds, the night becomes day).
   * Retires the old loop retrigger chains via a generation counter.
   */
  crossfadeToLoops(assets = [], fadeSec = 8) {
    if (!this.ctx || !this.masterGain || !this.running) return;
    const now = this.ctx.currentTime;
    const old = this.bedNodes;
    this.bedNodes = [];
    this._loopGen = (this._loopGen || 0) + 1; // stale scheduleFrom chains stop themselves
    old.forEach((n) => {
      try {
        if (n.gain) {
          n.gain.cancelScheduledValues(now);
          n.gain.setValueAtTime(n.gain.value || 0, now);
          n.gain.linearRampToValueAtTime(0.0001, now + fadeSec);
        }
      } catch (e) { /* ignore */ }
    });
    setTimeout(() => {
      old.forEach((n) => {
        try { n.stop && n.stop(); } catch (e) { /* ignore */ }
        try { n.disconnect && n.disconnect(); } catch (e) { /* ignore */ }
      });
    }, (fadeSec + 0.5) * 1000);
    this._startAmbientLoops(this._playableAssets(assets));
  }

  /**
   * Pause/resume the ambient experience alongside the dialogue.
   *
   * IMPORTANT: we do NOT call ctx.suspend() any more. Suspending the
   * AudioContext while a MediaStreamDestination is feeding a hidden <audio>
   * element causes the browser to stutter on the last sample fragment (the
   * audible "tttttt" loop on lock-screen pause). Instead we ramp masterGain
   * to 0 so the stream produces clean silence and resumes seamlessly.
   */
  async pause() {
    if (!this.ctx || !this.masterGain || !this.running) return;
    this._paused = true;
    this._userPaused = true; // Iter 86 — stop the lock-screen auto-resume loop
    // Freeze scheduled world events so nothing fires while paused.
    if (this._eventTimers) { this._eventTimers.forEach((t) => clearTimeout(t)); this._eventTimers = []; }
    this._savedDuckTarget = this.duckTarget;
    const now = this.ctx.currentTime;
    try {
      this.masterGain.gain.cancelScheduledValues(now);
      this.masterGain.gain.setValueAtTime(this.masterGain.gain.value || 0, now);
      this.masterGain.gain.linearRampToValueAtTime(0, now + 0.18);
    } catch (e) { console.warn("ambient: pause ramp failed", e); }
    if (this._voiceGain) {
      try {
        this._voiceGain.gain.cancelScheduledValues(now);
        this._voiceGain.gain.setValueAtTime(this._voiceGain.gain.value || 0, now);
        this._voiceGain.gain.linearRampToValueAtTime(0, now + 0.18);
      } catch (e) { /* ignore */ }
    }
  }

  async resume() {
    if (!this.ctx || !this.masterGain || !this.running) return;
    // The browser may have auto-suspended the context (lock screen,
    // backgrounded tab). Wake it before ramping back, otherwise the gain
    // ramp won't actually execute.
    if (this.ctx.state === "suspended") {
      try { await this.ctx.resume(); } catch (e) { console.warn("ambient: ctx.resume failed", e); }
    }
    this._paused = false;
    this._userPaused = false; // Iter 86 — re-arm the lock-screen auto-resume loop
    const target = this._savedDuckTarget != null ? this._savedDuckTarget : this.duckTarget;
    const now = this.ctx.currentTime;
    try {
      this.masterGain.gain.cancelScheduledValues(now);
      this.masterGain.gain.setValueAtTime(this.masterGain.gain.value || 0, now);
      this.masterGain.gain.linearRampToValueAtTime(target, now + 0.45);
    } catch (e) { console.warn("ambient: resume ramp failed", e); }
    // Re-arm the world-event scheduler that pause() cleared.
    if (this._lastAmbientEvents) this.scheduleAmbientEvents(this._lastAmbientEvents);
    if (this._voiceGain) {
      try {
        this._voiceGain.gain.cancelScheduledValues(now);
        this._voiceGain.gain.linearRampToValueAtTime(1, now + 0.45);
      } catch (e) { /* ignore */ }
    }
  }

  /**
   * Autoplay-proof voice path: when HTMLAudio playback is blocked by the
   * platform, the dialogue MP3 is decoded and played through the already
   * running WebAudio graph instead (same speaker/stream taps; bypasses the
   * ambience duck so the voice sits on top exactly like the element would).
   */
  async playVoiceUrl(url, { gain = 1.0, fadeInSec = 1.5, onEnded } = {}) {
    this.ensureContext();
    if (this.ctx.state === "suspended") {
      try { await this.ctx.resume(); } catch (e) { /* ignore */ }
    }
    this.stopVoice();
    const res = await fetch(url);
    const buf = await this.ctx.decodeAudioData(await res.arrayBuffer());
    const g = this.ctx.createGain();
    const now = this.ctx.currentTime;
    g.gain.setValueAtTime(0.0001, now);
    g.gain.linearRampToValueAtTime(gain, now + fadeInSec);
    g.connect(this.speakerGain);
    if (this.streamGain) g.connect(this.streamGain);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.connect(g);
    src.onended = () => {
      this._voiceSource = null;
      this._voiceGain = null;
      this._setBreathingDialogueDuck(false);
      try { if (onEnded) onEnded(); } catch (e) { /* ignore */ }
    };
    src.start();
    this._voiceSource = src;
    this._voiceGain = g;
    this._setBreathingDialogueDuck(true);
    return true;
  }

  stopVoice() {
    if (this._voiceSource) {
      try { this._voiceSource.onended = null; this._voiceSource.stop(); } catch (e) { /* ignore */ }
      this._voiceSource = null;
      this._voiceGain = null;
      this._setBreathingDialogueDuck(false);
    }
  }

  duck(mult = 0.35) {
    if (!this.ctx || !this.masterGain) return;
    // Dialogue is starting — suppress the breathing bed under the voice.
    this._setBreathingDialogueDuck(true);
    const target = this.masterLevel * mult;
    this.duckTarget = target;
    const now = this.ctx.currentTime;
    this.masterGain.gain.cancelScheduledValues(now);
    this.masterGain.gain.linearRampToValueAtTime(target, now + 1.4);
  }

  unduck(mult = 1.0) {
    if (!this.ctx || !this.masterGain) return;
    // Dialogue ended — after a short natural delay breathing swells back.
    this._setBreathingDialogueDuck(false);
    const target = this.masterLevel * mult;
    this.duckTarget = target;
    const now = this.ctx.currentTime;
    this.masterGain.gain.cancelScheduledValues(now);
    this.masterGain.gain.linearRampToValueAtTime(target, now + 2.5);
  }

  /**
   * Linearly ramp the master gain to `target` over `durationSec` seconds.
   * Used for the morning wake-up: a long, gentle volume rise.
   */
  setMaster(target, durationSec = 900) {
    if (!this.ctx || !this.masterGain) return;
    this.duckTarget = target;
    const now = this.ctx.currentTime;
    const current = this.masterGain.gain.value || 0;
    this.masterGain.gain.cancelScheduledValues(now);
    this.masterGain.gain.setValueAtTime(current, now);
    this.masterGain.gain.linearRampToValueAtTime(target, now + Math.max(1, durationSec));
  }

  /**
   * Session ending: gently drift the whole world to silence over a long
   * window instead of cutting off. The AudioContext stays alive (no teardown)
   * so the fade is perfectly smooth and the listener can drift off with it.
   * Random ambient events keep firing until the bed is inaudible.
   */
  fadeToSilence(durationSec = 300) {
    if (!this.ctx || !this.masterGain) return;
    this.duckTarget = 0;
    const now = this.ctx.currentTime;
    const current = this.masterGain.gain.value || 0;
    this.masterGain.gain.cancelScheduledValues(now);
    this.masterGain.gain.setValueAtTime(current, now);
    this.masterGain.gain.linearRampToValueAtTime(0.0001, now + Math.max(1, durationSec));
  }

  /**
   * Schedule random one-shot environmental events synthesized from atmosphere
   * tags (e.g. distant_thunder, owl, branch_crack, water_drip). These belong
   * to the WORLD, not the dialogue, and fire at slow random intervals so
   * the listener feels they have arrived inside a real place.
   *
   * Tags are matched loosely (substring). Unknown tags are skipped silently.
   * Idempotent: subsequent calls replace the schedule with the new tag set.
   */
  scheduleEvents(tags) {
    if (!this.ctx || !this.masterGain) return;
    if (this._eventTimers) this._eventTimers.forEach((t) => clearTimeout(t));
    this._eventTimers = [];
    if (!Array.isArray(tags) || tags.length === 0) return;

    const ctx = this.ctx;
    const dest = this.masterGain;

    // Each synthesizer is a tiny pure-WebAudio one-shot. They share a common
    // signature: emit a brief envelope over a few hundred ms / a second or two,
    // routed through masterGain so duck/pause/resume work transparently.
    const synths = {
      thunder: () => {
        const g = ctx.createGain();
        g.gain.value = 0;
        const lp = ctx.createBiquadFilter();
        lp.type = "lowpass"; lp.frequency.value = 200; lp.Q.value = 0.6;
        const src = ctx.createBufferSource();
        src.buffer = this._makeNoiseBuffer("brown", 5);
        src.connect(lp); lp.connect(g); g.connect(dest);
        const now = ctx.currentTime;
        g.gain.setValueAtTime(0, now);
        g.gain.linearRampToValueAtTime(0.32, now + 0.4);
        g.gain.linearRampToValueAtTime(0.18, now + 1.6);
        g.gain.linearRampToValueAtTime(0, now + 4.5);
        src.start(now); src.stop(now + 4.6);
      },
      owl: () => {
        const g = ctx.createGain();
        const osc = ctx.createOscillator();
        osc.type = "sine"; osc.frequency.value = 380 + Math.random() * 60;
        osc.connect(g); g.connect(dest);
        const now = ctx.currentTime;
        // two soft hoots
        g.gain.setValueAtTime(0, now);
        g.gain.linearRampToValueAtTime(0.08, now + 0.05);
        g.gain.linearRampToValueAtTime(0, now + 0.4);
        g.gain.linearRampToValueAtTime(0.07, now + 0.85);
        g.gain.linearRampToValueAtTime(0, now + 1.3);
        osc.start(now); osc.stop(now + 1.4);
      },
      drip: () => {
        const g = ctx.createGain();
        const osc = ctx.createOscillator();
        osc.type = "sine"; osc.frequency.value = 1200 + Math.random() * 400;
        osc.connect(g); g.connect(dest);
        const now = ctx.currentTime;
        g.gain.setValueAtTime(0, now);
        g.gain.linearRampToValueAtTime(0.06, now + 0.005);
        g.gain.exponentialRampToValueAtTime(0.0001, now + 0.25);
        osc.start(now); osc.stop(now + 0.3);
      },
      crack: () => {
        const g = ctx.createGain();
        const src = ctx.createBufferSource();
        src.buffer = this._makeNoiseBuffer("pink", 1);
        const bp = ctx.createBiquadFilter();
        bp.type = "bandpass"; bp.frequency.value = 1200; bp.Q.value = 2;
        src.connect(bp); bp.connect(g); g.connect(dest);
        const now = ctx.currentTime;
        g.gain.setValueAtTime(0, now);
        g.gain.linearRampToValueAtTime(0.18, now + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
        src.start(now); src.stop(now + 0.2);
      },
      pop: () => {
        const g = ctx.createGain();
        const osc = ctx.createOscillator();
        osc.type = "triangle";
        osc.frequency.value = 600 + Math.random() * 400;
        osc.connect(g); g.connect(dest);
        const now = ctx.currentTime;
        g.gain.setValueAtTime(0, now);
        g.gain.linearRampToValueAtTime(0.08, now + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
        osc.start(now); osc.stop(now + 0.2);
      },
      gust: () => {
        const g = ctx.createGain();
        const src = ctx.createBufferSource();
        src.buffer = this._makeNoiseBuffer("brown", 6);
        const lp = ctx.createBiquadFilter();
        lp.type = "lowpass"; lp.frequency.value = 400;
        src.connect(lp); lp.connect(g); g.connect(dest);
        const now = ctx.currentTime;
        g.gain.setValueAtTime(0, now);
        g.gain.linearRampToValueAtTime(0.15, now + 1.2);
        g.gain.linearRampToValueAtTime(0, now + 4.5);
        src.start(now); src.stop(now + 4.6);
      },
      gull: () => {
        const g = ctx.createGain();
        const osc = ctx.createOscillator();
        osc.type = "sawtooth";
        osc.frequency.setValueAtTime(900, ctx.currentTime);
        osc.frequency.linearRampToValueAtTime(1300, ctx.currentTime + 0.3);
        osc.frequency.linearRampToValueAtTime(700, ctx.currentTime + 0.7);
        const lp = ctx.createBiquadFilter();
        lp.type = "lowpass"; lp.frequency.value = 2400;
        osc.connect(lp); lp.connect(g); g.connect(dest);
        const now = ctx.currentTime;
        g.gain.setValueAtTime(0, now);
        g.gain.linearRampToValueAtTime(0.04, now + 0.1);
        g.gain.linearRampToValueAtTime(0, now + 0.85);
        osc.start(now); osc.stop(now + 0.9);
      },
      clock: () => {
        const g = ctx.createGain();
        const osc = ctx.createOscillator();
        osc.type = "square"; osc.frequency.value = 2400;
        osc.connect(g); g.connect(dest);
        const now = ctx.currentTime;
        g.gain.setValueAtTime(0, now);
        g.gain.linearRampToValueAtTime(0.03, now + 0.002);
        g.gain.exponentialRampToValueAtTime(0.0001, now + 0.04);
        osc.start(now); osc.stop(now + 0.05);
      },
    };

    // tag → (synthName, [minSec, maxSec] between events)
    const map = [
      [/thunder/, "thunder", 35, 80],
      [/owl|distant_animal/, "owl", 28, 70],
      [/drip|water/, "drip", 18, 45],
      [/crack|branch/, "crack", 25, 70],
      [/pop|ember|fire_pop|low_fire|fireplace/, "pop", 6, 22],
      [/wind|gust|breeze|high_wind|pine_wind/, "gust", 22, 60],
      [/wave|surf/, "gust", 14, 30],
      [/gull|bird/, "gull", 30, 90],
      [/clock|tick/, "clock", 12, 18],
      [/heater|hum|muffled_city/, "gust", 60, 140],
      [/page_turn|scale_rustle|tail_shift|soft_coil|leather_creak|chair_creak|wood_creak|stone_creak|blanket/, "crack", 40, 110],
    ];

    const schedule = (synthName, minSec, maxSec) => {
      const next = (minSec + Math.random() * (maxSec - minSec)) * 1000;
      const t = setTimeout(() => {
        if (!this.running) return;
        try { synths[synthName](); } catch (e) { console.warn("event synth failed", synthName, e); }
        if (this.running) schedule(synthName, minSec, maxSec);
      }, next);
      this._eventTimers.push(t);
    };

    // Dedup synths so we don't double-fire (e.g. "fireplace_crackle" + "low_fire" → one pop loop)
    const chosen = new Set();
    tags.forEach((tag) => {
      const t = String(tag).toLowerCase();
      for (const [rx, synthName, mn, mx] of map) {
        if (rx.test(t) && !chosen.has(synthName)) {
          chosen.add(synthName);
          schedule(synthName, mn, mx);
          break;
        }
      }
    });
  }

  /**
   * MVP sound-library scheduler. Ambient Events are random world-owned events
   * selected by category probability/cooldowns. Scenario Events are NOT handled
   * here because they must be triggered explicitly by the story/manifest.
   */
  // Iter 109 — PROBABILISTIC AMBIENT EVENTS. Events are OPTIONAL: each
  // category first rolls whether it appears AT ALL tonight (owl roughly one
  // night in three), then how many times (mostly once, sometimes twice, far
  // apart). The roll happens ONCE per session and survives pause/resume —
  // "Oh… there's an owl tonight", never "here comes the owl again".
  scheduleAmbientEvents(ambientEvents) {
    if (!this.ctx || !this.masterGain) return;
    this._lastAmbientEvents = ambientEvents; // remember so we can resume after pause
    if (this._eventTimers) this._eventTimers.forEach((t) => clearTimeout(t));
    this._eventTimers = [];
    const assets = ambientEvents?.assets || [];
    const categories = ambientEvents?.categories || [];
    if (!assets.length && categories.length && this.soundPlan?.procedural_replacement_enabled) {
      this.scheduleEvents(categories);
      return;
    }
    const byCategory = new Map();
    assets.forEach((asset) => {
      if (!asset?.category) return;
      if (!byCategory.has(asset.category)) byCategory.set(asset.category, []);
      byCategory.get(asset.category).push(asset);
    });
    const thunderProgression = this.soundPlan?.weather_progression?.thunder;
    if (thunderProgression && byCategory.has("thunder")) {
      this._scheduleThunderProgression(thunderProgression, byCategory.get("thunder"));
      byCategory.delete("thunder");
    }
    if (!this._eventSessionPlan) this._eventSessionPlan = new Map(); // category → { allowed, played }
    byCategory.forEach((variants, category) => {
      let plan = this._eventSessionPlan.get(category);
      if (!plan) {
        const t0 = variants[0] || {};
        const presence = t0.probability == null
          ? this._defaultEventPresence(category)
          : Math.max(0.05, Math.min(1, t0.probability));
        const present = Math.random() <= presence;
        plan = { allowed: present ? (Math.random() < 0.3 ? 2 : 1) : 0, played: 0 };
        this._eventSessionPlan.set(category, plan);
        console.debug(`[MIXER] ambient event '${category}': ${plan.allowed ? `${plan.allowed}x tonight` : "silent tonight"}`);
      }
      const remaining = plan.allowed - plan.played;
      if (remaining <= 0) return;
      let at = 90 + Math.random() * 300; // first occurrence 1.5-6.5 min in
      for (let i = 0; i < remaining; i++) {
        const timer = setTimeout(() => {
          if (!this.running || this._paused || this._userPaused) return;
          if (plan.played >= plan.allowed) return;
          plan.played += 1;
          const template = variants[Math.floor(Math.random() * variants.length)] || {};
          const url = template.storage?.url;
          if (url) this.playOneShot(url, template.target_gain || 0.16);
          if (this.onEvent) { try { this.onEvent(category, { url }); } catch (e) { /* noop */ } }
        }, at * 1000);
        this._eventTimers.push(timer);
        at += 180 + Math.random() * 260; // a possible second one, 3-7+ min later
      }
    });
  }

  _defaultEventPresence(category) {
    if (category === "owl") return 0.35;
    if (category === "animals") return 0.4;
    if (category === "birds") return 0.8; // dawn chorus is expected in the morning
    return 0.45;
  }

  _scheduleThunderProgression(progress, variants = []) {
    const sequence = progress?.events || [];
    if (!sequence.length || !variants.length) return;
    sequence.forEach((item) => {
      const [minSec, maxSec] = item.scheduled_window_sec || [35, 120];
      const delay = (minSec + Math.random() * Math.max(1, maxSec - minSec)) * 1000;
      const timer = setTimeout(() => {
        if (!this.running || this._paused) return;
        const distance = item.distance;
        const matching = variants.filter((asset) => String(asset.variant || "").toLowerCase().includes(distance));
        const pool = matching.length ? matching : variants;
        const chosen = pool[Math.floor(Math.random() * pool.length)] || {};
        const url = chosen.storage?.url;
        if (url) this.playOneShot(url, chosen.target_gain || 0.16);
        if (this.onEvent) { try { this.onEvent("thunder", { url }); } catch (e) { /* noop */ } }
      }, delay);
      this._eventTimers.push(timer);
    });
  }

  // HEARTBEAT — conditional, intimate. Only started by the runtime once the
  // scene establishes a close chest-resting embrace (after dialogue). It fades
  // in over ~2.5s and its loops NEVER overlap (rhythmic): the next beat-loop
  // starts only when the previous one ends. stopHeartbeat() fades out over ~2.5s.
  async startHeartbeat(url, gain = 0.5) {
    if (!this.ctx || !this.masterGain || !url || this._heartbeatOn) return;
    this._heartbeatOn = true;
    let buffer;
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      buffer = await this.ctx.decodeAudioData(await res.arrayBuffer());
    } catch (e) {
      this._heartbeatOn = false;
      return;
    }
    if (!this._heartbeatOn) return; // stopped while fetching
    const g = this.ctx.createGain();
    const now = this.ctx.currentTime;
    g.gain.setValueAtTime(0.0001, now);
    g.gain.linearRampToValueAtTime(Math.max(0.001, gain), now + 2.5);
    g.connect(this.masterGain);
    this._heartbeatGain = g;
    const playNext = () => {
      if (!this._heartbeatOn || !this.ctx) return;
      const src = this.ctx.createBufferSource();
      src.buffer = buffer;
      src.connect(g);
      src.onended = () => { if (this._heartbeatOn) playNext(); };
      src.start();
      this._heartbeatSrc = src;
    };
    playNext();
  }

  stopHeartbeat() {
    if (!this._heartbeatOn) return;
    this._heartbeatOn = false;
    const g = this._heartbeatGain;
    const src = this._heartbeatSrc;
    if (g && this.ctx) {
      const now = this.ctx.currentTime;
      try {
        g.gain.cancelScheduledValues(now);
        g.gain.setValueAtTime(g.gain.value, now);
        g.gain.linearRampToValueAtTime(0.0001, now + 2.5);
      } catch (e) { /* ignore */ }
    }
    setTimeout(() => {
      try { if (src) { src.onended = null; src.stop(); } } catch (e) { /* ignore */ }
      try { if (g) g.disconnect(); } catch (e) { /* ignore */ }
    }, 2800);
    this._heartbeatSrc = null;
    this._heartbeatGain = null;
  }

  // Iter 108 — presence assets handed over by the session host so the cuddle
  // state can start persistent loops on its own.
  setPresenceAssets({ heartbeatUrl = null, breathingLoops = [], passiveCoiling = [] } = {}) {
    this._presenceAssets = { heartbeatUrl, breathingLoops, passiveCoiling };
  }

  // BREATHING — persistent biological presence, SHARED across characters.
  // Loops seamlessly until the session ends. Level is loudness-normalized
  // from the decoded buffer (see BREATHING_TARGET_RMS above); `intensity`
  // is a relative multiplier where 1.0 = the calibrated level.
  async startBreathingLoop(url, intensity = 1.0) {
    if (!this.ctx || !this.masterGain || !url || this._breathingOn) return;
    this._breathingOn = true;
    try {
      const { buffer } = await this._getBuffer(url);
      if (!this._breathingOn) return;
      let acc = 0, n = 0;
      const step = Math.max(1, Math.floor(buffer.length / 200000));
      for (let c = 0; c < buffer.numberOfChannels; c += 1) {
        const d = buffer.getChannelData(c);
        for (let i = 0; i < d.length; i += step) { acc += d[i] * d[i]; n += 1; }
      }
      const rms = Math.sqrt(acc / Math.max(1, n));
      const base = Math.min(BREATHING_MAX_GAIN, BREATHING_TARGET_RMS / Math.max(rms, 1e-5)) * intensity;
      this._breathingBase = base;
      const startLevel = this._breathingDucked ? base * BREATHING_DUCK_MULT : base;
      const g = this.ctx.createGain();
      const now = this.ctx.currentTime;
      g.gain.setValueAtTime(0.0001, now);
      g.gain.linearRampToValueAtTime(Math.max(0.001, startLevel), now + 3);
      g.connect(this.masterGain);
      const src = this.ctx.createBufferSource();
      src.buffer = buffer;
      src.loop = true;
      src.connect(g);
      src.start();
      this._breathingSrc = src;
      this._breathingGain = g;
      console.debug("[MIXER] breathing level normalized", { source_rms: rms.toFixed(5), gain: base.toFixed(2), ducked: !!this._breathingDucked });
    } catch (e) {
      this._breathingOn = false;
    }
  }

  // Public alias for pages that drive dialogue through an HTMLAudio element.
  setDialogueActive(active) {
    this._setBreathingDialogueDuck(!!active);
  }

  // Dialogue-responsive breathing: suppressed while the companion speaks,
  // swelling back after a short natural delay once the line ends. Smooth
  // ramps only — never on/off switching.
  _setBreathingDialogueDuck(active) {
    this._breathingDucked = !!active;
    if (this._breathingRestoreTimer) { clearTimeout(this._breathingRestoreTimer); this._breathingRestoreTimer = null; }
    if (!this._breathingOn || !this._breathingGain || !this.ctx || this._breathingBase == null) return;
    const rampTo = (level, sec) => {
      const g = this._breathingGain;
      if (!g || !this.ctx) return;
      const now = this.ctx.currentTime;
      try {
        g.gain.cancelScheduledValues(now);
        g.gain.setValueAtTime(Math.max(0.0001, g.gain.value || 0.0001), now);
        g.gain.linearRampToValueAtTime(Math.max(0.0001, level), now + sec);
      } catch (e) { /* ignore */ }
    };
    if (active) {
      rampTo(this._breathingBase * BREATHING_DUCK_MULT, 0.9);
      console.debug("[MIXER] breathing ducked (dialogue playing)");
    } else {
      this._breathingRestoreTimer = setTimeout(() => {
        if (this._breathingOn && !this._breathingDucked) {
          rampTo(this._breathingBase, 2.6);
          console.debug("[MIXER] breathing audible (dialogue silent)");
        }
      }, 1500);
    }
  }

  // Pick ONE random breathing variant and start it; if the pick fails to
  // decode (e.g. an AIFF-in-.mp3 asset on Chrome), fall through to the next
  // variant. Never overlaps — startBreathingLoop guards _breathingOn.
  async startBreathingBed(list, intensity = 1.0) {
    if (this._breathingOn) return;
    const pool = (list || []).filter((l) => l && l.url);
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }
    for (const pick of pool) {
      await this.startBreathingLoop(pick.url, intensity);
      if (this._breathingOn) {
        console.debug("[MIXER] breathing presence bed started", pick.url.split("?")[0].split("/").pop());
        return;
      }
    }
  }

  stopBreathingLoop() {
    if (this._breathingRestoreTimer) { clearTimeout(this._breathingRestoreTimer); this._breathingRestoreTimer = null; }
    this._breathingBase = null;
    if (!this._breathingOn) return;
    this._breathingOn = false;
    const g = this._breathingGain;
    const src = this._breathingSrc;    if (g && this.ctx) {
      const now = this.ctx.currentTime;
      try {
        g.gain.cancelScheduledValues(now);
        g.gain.setValueAtTime(g.gain.value, now);
        g.gain.linearRampToValueAtTime(0.0001, now + 2.5);
      } catch (e) { /* ignore */ }
    }
    setTimeout(() => {
      try { if (src) src.stop(); } catch (e) { /* ignore */ }
      try { if (g) g.disconnect(); } catch (e) { /* ignore */ }
    }, 2800);
    this._breathingSrc = null;
    this._breathingGain = null;
  }

  // Character presence: continuous "living presence" loops (e.g. heartbeat)
  // start immediately and sit low under everything; presence one-shot events
  // (e.g. Serine's soft hiss) surface sparsely, like natural breathing.
  schedulePresenceEvents(presence) {
    if (!this.ctx || !this.masterGain || !presence) return;
    // ONE random asset per category — never every file at once (a category
    // like serine/breathing-serine ships 5 variants of the SAME loop).
    const byCat = {};
    (presence.loops || []).forEach((l) => {
      if (!l || !l.url) return;
      const cat = String(l.category || "loop");
      (byCat[cat] = byCat[cat] || []).push(l);
    });
    Object.entries(byCat).forEach(([cat, list]) => {
      const pick = list[Math.floor(Math.random() * list.length)];
      if (/breathing/i.test(cat)) {
        // Sleeping-breathing bed: ultra-subtle, single instance.
        this.startBreathingBed(list);
      } else {
        this.playSampleLoop(pick.url, pick.gain != null ? pick.gain : 0.2);
      }
    });
    const events = presence.events || [];
    if (!events.length) return;
    const minGap = (presence.min_gap_sec || 60) * 1000;
    const maxGap = (presence.max_gap_sec || 120) * 1000;
    const schedule = () => {
      const wait = minGap + Math.random() * Math.max(1, maxGap - minGap);
      this._presenceEventTimer = setTimeout(() => {
        if (this.running && !this._paused) {
          const ev = events[Math.floor(Math.random() * events.length)];
          if (ev && ev.url) this.playOneShot(ev.url, ev.gain != null ? ev.gain : 0.14);
        }
        if (this.running) schedule();
      }, wait);
    };
    schedule();
  }

  // Character scenario one-shots (e.g. slithering, footsteps) — occasional
  // physical actions, more spaced out than presence, blended into the world.
  scheduleScenarioEvents(assets, minGapSec = 90, maxGapSec = 210) {
    if (!this.ctx || !this.masterGain || !assets || !assets.length) return;
    const minGap = minGapSec * 1000;
    const maxGap = maxGapSec * 1000;
    const schedule = () => {
      const wait = minGap + Math.random() * Math.max(1, maxGap - minGap);
      this._scenarioTimer = setTimeout(() => {
        if (this.running && !this._paused) {
          const a = assets[Math.floor(Math.random() * assets.length)];
          if (a && a.url) this.playOneShot(a.url, a.gain != null ? a.gain : 0.18);
        }
        if (this.running) schedule();
      }, wait);
    };
    schedule();
  }

  // Iter 87 — SHARED AMBIENT PRESENCE EVENTS (Presence Events v1).
  // Room-level one-shots (blanket rustle, mattress shift) that make the
  // companion's physical presence audible. Sparse + randomized: 0-2 events
  // on short sessions, 1-4 on longer ones, never the same category twice in
  // a row, never near the session edges. Returns the planned schedule.
  scheduleSharedPresenceEvents(pack, durationSec) {
    const events = (pack && pack.events) || [];
    if (!this.ctx || !events.length || !durationSec) return [];
    const pol = pack.policy || {};
    const isLong = durationSec > (pol.short_session_max_min || 20) * 60;
    const [lo, hi] = isLong ? (pol.long_count || [1, 4]) : (pol.short_count || [0, 2]);
    const count = lo + Math.floor(Math.random() * (hi - lo + 1));
    if (count <= 0) return [];
    const startSec = pol.avoid_first_sec || 90;
    const endSec = Math.max(startSec + 30, durationSec - (pol.avoid_last_sec || 60));
    const minGap = pol.min_gap_sec || 150;
    const times = [];
    let guard = 0;
    while (times.length < count && guard++ < 80) {
      const t = startSec + Math.random() * (endSec - startSec);
      if (times.every((x) => Math.abs(x - t) >= minGap)) times.push(t);
    }
    times.sort((a, b) => a - b);
    // Iter 95 — BUGFIX: these timers used to live in `_eventTimers`, which
    // scheduleAmbientEvents() WIPES on every (re)schedule — pause/resume and
    // the morning night→morning crossfade silently killed every pending
    // blanket-rustle / mattress-shift. Presence events own their timers now.
    if (this._presenceEventTimers) this._presenceEventTimers.forEach((t) => clearTimeout(t));
    this._presenceEventTimers = [];
    // Iter 100 — Composite Presence Events: one physical action = an ordered,
    // synchronized sound sequence, played as ONE unit (never split). The
    // event/composite choice happens lazily AT FIRE TIME so that late-arriving
    // session context (high-intimacy allowance) still applies.
    const composites = (pack && pack.composites) || [];
    // Iter 105 — cuddle-state pool: every physical sound we can replay while
    // she stays coiled around the listener (tail adjustments, blanket,
    // mattress, breathing). Used by enterCuddleState().
    this._cuddlePool = [
      ...events.map((e) => ({ url: e.url, gain: Math.min(e.gain != null ? e.gain : 0.3, 0.5), category: e.category })),
      ...composites.flatMap((c) => (c.intimacy === "high" ? [] : (c.steps || []).map((s) => ({ url: s.url, gain: s.gain, category: s.category })))),
      ...((pack && pack.breathing) || []).map((b) => ({ url: b.url, gain: 0.14, category: "breathing" })),
    ].filter((x) => x.url);
    let lastCat = null;
    const schedule = [];
    const pickComposite = () => {
      const pool = composites.filter(
        (c) => c.id !== lastCat && (c.intimacy !== "high" || (this._allowHighIntimacy && Math.random() < 0.35)),
      );
      if (!pool.length) return null;
      const totalW = pool.reduce((s, c) => s + (c.weight || 1), 0);
      let r = Math.random() * totalW;
      for (const c of pool) {
        r -= c.weight || 1;
        if (r <= 0) return c;
      }
      return pool[pool.length - 1];
    };
    for (const t of times) {
      const timer = setTimeout(() => {
        if (!this.running || this._paused || this._userPaused) return;
        // Iter 108 — once cuddled, movement-confirmation stops: presence is
        // maintained by heartbeat/breathing + rare micro-events instead.
        if (this._cuddleActive) return;
        const comp = composites.length && Math.random() < (this._compositeChance != null ? this._compositeChance : 0.5) ? pickComposite() : null;
        if (comp) {
          lastCat = comp.id;
          this.playComposite(comp);
          return;
        }
        let pool = events.filter((e) => e.category !== lastCat);
        if (!pool.length) pool = events;
        const ev = pool[Math.floor(Math.random() * pool.length)];
        lastCat = ev.category;
        this.playOneShot(ev.url, Math.min(ev.gain != null ? ev.gain : 0.3, 0.5), { intimate: true });
      }, t * 1000);
      this._presenceEventTimers.push(timer);
      schedule.push({ at_sec: Math.round(t) });
    }
    return schedule;
  }

  // Iter 100 — session-level allowance for rare high-intimacy composites
  // (hair touch). Set by the session host once the session doc arrives.
  // Iter 103b — breathing presence bed. Sparse, ultra-soft one-shots between
  // speech so presence eventually comes from breathing as well as dialogue.
  // No-op until breathing assets exist in the pack — zero future changes
  // needed beyond uploading the sounds.
  scheduleBreathingBed(assets, durationSec, { avoidFirstSec = 60, minGapSec = 25, maxGapSec = 50, gain = 0.14 } = {}) {
    if (!assets || !assets.length || !durationSec) return 0;
    if (!this._presenceEventTimers) this._presenceEventTimers = [];
    let t = avoidFirstSec + Math.random() * (maxGapSec - minGapSec);
    let count = 0;
    while (t < durationSec - 20) {
      const at = t;
      const timer = setTimeout(() => {
        if (!this.running || this._paused || this._userPaused) return;
        const a = assets[Math.floor(Math.random() * assets.length)];
        this.playOneShot(a.url, gain, { intimate: true });
      }, at * 1000);
      this._presenceEventTimers.push(timer);
      count += 1;
      t += minGapSec + Math.random() * (maxGapSec - minGapSec);
    }
    return count;
  }

  // Iter 103 — session-level presence profile: rare high-intimacy composites
  // (hair touch) + composite share (deep relationships feel physically closer:
  // more coiling / blanket / mattress composites instead of lone rustles).
  setPresenceProfile({ highIntimacy, compositeChance } = {}) {
    if (highIntimacy !== undefined) this._allowHighIntimacy = !!highIntimacy;
    if (compositeChance !== undefined) this._compositeChance = compositeChance;
  }

  setHighIntimacyPresence(allowed) {
    this._allowHighIntimacy = !!allowed;
  }

  // Iter 100 — play a Composite Presence Event: every step fires on its own
  // delay but the whole thing is ONE action; steps are never played
  // independently of each other.
  playComposite(comp) {
    for (const step of comp.steps || []) {
      const timer = setTimeout(() => {
        if (this.running && !this._paused && !this._userPaused) {
          this.playOneShot(step.url, Math.min(step.gain != null ? step.gain : 0.28, 0.5), { intimate: true });
        }
      }, step.delay_ms || 0);
      if (this._presenceEventTimers) this._presenceEventTimers.push(timer);
    }
    // Iter 105 — a coiling composite means she is now physically wrapped
    // around the listener: enter the cuddle state (recurring subtle presence).
    if ((comp.steps || []).some((s) => s.category === "coiling")) this.enterCuddleState();
  }

  // Iter 108 — CUDDLE STATE v2: movement-confirmation → STABLE BIOLOGICAL
  // PRESENCE. Establishment sequence: blanket rustle → fabric movement →
  // heartbeat loop starts → breathing loop starts. Heartbeat + breathing then
  // run continuously until the session ends. Movement becomes RARE
  // micro-events (passive coiling / tiny blanket / mattress, every ~90-170s):
  // she has settled comfortably and is simply lying beside the listener.
  // Idempotent; cleared on stop().
  enterCuddleState() {
    if (this._cuddleActive || !this.ctx) return;
    this._cuddleActive = true;
    if (typeof this.onCuddleEstablished === "function") {
      try { this.onCuddleEstablished(); } catch (e) { /* ignore */ }
    }
    const assets = this._presenceAssets || {};
    // Active coiling (the wrap) is transition-only — never part of the
    // settled state. Passive coiling joins the micro pool, weighted 2x.
    const pool = (this._cuddlePool || []).filter((x) => x.category !== "coiling");
    const passive = (assets.passiveCoiling || [])
      .filter((a) => a.url)
      .map((a) => ({ url: a.url, gain: Math.min(a.gain != null ? a.gain : 0.22, 0.3), category: "coiling-passive" }));
    this._cuddleMicroPool = [...passive, ...passive, ...pool];
    const fabric = pool.filter((x) => /blanket|fabric|hair/.test(String(x.category || "")));
    if (!this._presenceEventTimers) this._presenceEventTimers = [];
    const at = (ms, fn) => {
      const t = setTimeout(() => { if (this.running && !this._paused && !this._userPaused) fn(); }, ms);
      this._presenceEventTimers.push(t);
    };
    const basePan = -0.2 + Math.random() * 0.4;
    console.debug("[MIXER] cuddle established — settling sequence begins");
    // --- establishment: she settles in ---
    if (fabric.length) {
      at(0, () => this.playOneShot(fabric[Math.floor(Math.random() * fabric.length)].url, 0.3, { intimate: true, pan: basePan }));
      at(1500, () => this.playOneShot(fabric[Math.floor(Math.random() * fabric.length)].url, 0.2, { intimate: true, pan: basePan + 0.06 }));
    }
    at(3000, () => {
      if (assets.heartbeatUrl) this.startHeartbeat(assets.heartbeatUrl, 0.4);
    });
    at(4600, () => {
      this.startBreathingBed(assets.breathingLoops || []);
    });
    // --- settled: rare micro-adjustments only ---
    const schedule = () => {
      this._cuddleTimer = setTimeout(() => {
        if (!this.running) return;
        if (!this._paused && !this._userPaused) this._playCuddleMicroEvent();
        schedule();
      }, (90 + Math.random() * 80) * 1000);
    };
    schedule();
  }

  _playCuddleMicroEvent() {
    const pool = this._cuddleMicroPool || [];
    if (!pool.length) return;
    const pick = pool[Math.floor(Math.random() * pool.length)];
    const level = pick.category === "coiling-passive" ? 1 : 0.55;
    console.debug("[MIXER] cuddle micro-event:", pick.category);
    this.playOneShot(
      pick.url,
      Math.min((pick.gain != null ? pick.gain : 0.28) * level, 0.4),
      { intimate: true, pan: -0.15 + Math.random() * 0.3 },
    );
  }

  // Iter 106 — decoded-buffer cache + loudness analysis (RMS/peak).
  async _getBuffer(url) {
    if (!this._bufferCache) this._bufferCache = new Map();
    if (this._bufferCache.has(url)) return this._bufferCache.get(url);
    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();
    const buffer = await this.ctx.decodeAudioData(arrayBuffer);
    const data = buffer.getChannelData(0);
    const stride = Math.max(1, Math.floor(data.length / 20000));
    let sum = 0;
    let peak = 0;
    let n = 0;
    for (let i = 0; i < data.length; i += stride) {
      const v = data[i];
      sum += v * v;
      if (v > peak) peak = v;
      else if (-v > peak) peak = -v;
      n++;
    }
    const entry = { buffer, rms: Math.sqrt(sum / Math.max(1, n)), peak };
    if (this._bufferCache.size > 40) this._bufferCache.clear();
    this._bufferCache.set(url, entry);
    return entry;
  }

  // Iter 105 — `intimate` puts the sound immediately BESIDE the listener:
  // a warm low-shelf, a slight random stereo position (she moves next to you,
  // not across the room) and a 2x presence boost so soft foley reads close.
  // Iter 106 — LOUDNESS NORMALIZATION (preserves dynamic range): quiet foley
  // recordings are lifted toward a reference RMS so `gain` means the same
  // perceived level for every asset; already-hot files are left untouched
  // (norm >= 1 only) and a peak guard prevents any clipping. No compression.
  async playOneShot(url, gain = 0.16, opts = {}) {
    if (!this.ctx || !this.masterGain || !url) return;
    if (this._badUrls && this._badUrls.has(url)) return; // Iter 106 — don't retry dead assets
    try {
      const { buffer, rms, peak } = await this._getBuffer(url);
      const REF_RMS = 0.14;
      const norm = rms > 0.0005 ? Math.min(6, Math.max(1, REF_RMS / rms)) : 1;
      const boost = opts.intimate ? 2.0 : 1.2;
      let g = Math.min(1, Math.max(0, gain)) * norm * boost;
      if (peak > 0 && g * peak > 0.85) g = 0.85 / peak;
      const src = this.ctx.createBufferSource();
      const gainNode = this.ctx.createGain();
      src.buffer = buffer;
      let tail = src;
      if (opts.intimate) {
        const shelf = this.ctx.createBiquadFilter();
        shelf.type = "lowshelf";
        shelf.frequency.value = 320;
        shelf.gain.value = 4.5;
        tail.connect(shelf);
        tail = shelf;
        if (this.ctx.createStereoPanner) {
          const pan = this.ctx.createStereoPanner();
          pan.pan.value = typeof opts.pan === "number" ? opts.pan : (Math.random() * 0.5 - 0.25);
          tail.connect(pan);
          tail = pan;
        }
      }
      gainNode.gain.value = Math.max(0, Math.min(1, g));
      tail.connect(gainNode);
      gainNode.connect(this.masterGain);
      src.start();
      src.stop(this.ctx.currentTime + buffer.duration + 0.05);
    } catch (e) {
      if (!this._badUrls) this._badUrls = new Set();
      this._badUrls.add(url);
      console.warn("ambient event asset failed", e);
    }
  }

  triggerScenarioEvent(category, scenarioEvents) {
    const assets = scenarioEvents?.assets || this.soundPlan?.scenario_events?.assets || [];
    const matches = assets.filter((asset) => asset.category === category && asset.storage?.url);
    if (!matches.length) return false;
    const chosen = matches[Math.floor(Math.random() * matches.length)];
    // Iter 105 — coiling is a body-contact sound: intimate path + cuddle state.
    const isCoiling = category === "coiling";
    this.playOneShot(chosen.storage.url, chosen.target_gain || 0.16, { intimate: isCoiling });
    if (isCoiling) this.enterCuddleState();
    return true;
  }

  async stop() {
    if (!this.ctx) return;
    this.running = false;
    if (this._lockWatchdogTimer) { clearInterval(this._lockWatchdogTimer); this._lockWatchdogTimer = null; }
    if (this._resumeRetryTimer) { clearInterval(this._resumeRetryTimer); this._resumeRetryTimer = null; }
    if (this.presenceTimer) clearTimeout(this.presenceTimer);
    if (this._presenceEventTimers) { this._presenceEventTimers.forEach((t) => clearTimeout(t)); this._presenceEventTimers = []; }
    if (this._cuddleTimer) { clearTimeout(this._cuddleTimer); this._cuddleTimer = null; }
    this._cuddleActive = false;
    this._eventSessionPlan = null; // Iter 109 — next session re-rolls its own night
    if (this._presenceEventTimer) { clearTimeout(this._presenceEventTimer); this._presenceEventTimer = null; }
    if (this._scenarioTimer) { clearTimeout(this._scenarioTimer); this._scenarioTimer = null; }
    if (this._heartbeatOn) {
      this._heartbeatOn = false;
      try { if (this._heartbeatSrc) { this._heartbeatSrc.onended = null; this._heartbeatSrc.stop(); } } catch (e) { /* ignore */ }
      try { if (this._heartbeatGain) this._heartbeatGain.disconnect(); } catch (e) { /* ignore */ }
      this._heartbeatSrc = null;
      this._heartbeatGain = null;
    }
    if (this._breathingOn) {
      this._breathingOn = false;
      try { if (this._breathingSrc) this._breathingSrc.stop(); } catch (e) { /* ignore */ }
      try { if (this._breathingGain) this._breathingGain.disconnect(); } catch (e) { /* ignore */ }
      this._breathingSrc = null;
      this._breathingGain = null;
    }
    if (this._eventTimers) { this._eventTimers.forEach((t) => clearTimeout(t)); this._eventTimers = []; }
    if (this._loopTimers) { this._loopTimers.forEach((t) => clearTimeout(t)); this._loopTimers = []; }
    if (this._visHandler) {
      try { document.removeEventListener("visibilitychange", this._visHandler); } catch (e) { /* ignore */ }
      this._visHandler = null;
    }
    if (this._bgAudioEl) {
      try { this._bgAudioEl.pause(); this._bgAudioEl.srcObject = null; } catch (e) { /* ignore */ }
      this._bgAudioEl = null;
    }
    const now = this.ctx.currentTime;
    try {
      this.masterGain.gain.cancelScheduledValues(now);
      this.masterGain.gain.linearRampToValueAtTime(0, now + 1.5);
    } catch (e) { console.warn("ambient: fade-out failed", e); }
    setTimeout(() => {
      this.bedNodes.forEach((n) => {
        try { n.stop && n.stop(); } catch (e) { console.warn("ambient: node stop failed", e); }
        try { n.disconnect && n.disconnect(); } catch (e) { console.warn("ambient: node disconnect failed", e); }
      });
      this.bedNodes = [];
      try { this.ctx.close(); } catch (e) { console.warn("ambient: ctx.close failed", e); }
      this.ctx = null;
      this.masterGain = null;
    }, 1700);
  }
}

export default AmbientMixer;
